import { Checkbox, FormControlLabel, FormGroup, Stack } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { inferParserType } from 'nuqs';
import React, { memo, useCallback } from 'react';
import { Writable } from 'type-fest';
import { FilterByValue, FromArray } from '../../../utils/type-helpers';
import Collapsible from '../../common/Collapsible';
import { BuildingFilters } from '../BuildingFilters';
import { useBuildingFilters } from '../BuildingFiltersProvider';
import FilterValueLabel from '../FilterValueLabel';
import { BUILDING_FILTERS_SEARCH_PARAMS, BuildingFiltersSearchParams } from '../search-params';

type CheckboxSearchParamKeys = Extract<
  BuildingFiltersSearchParams,
  | 'portfolioIds'
  | 'economicUnitIds'
  | 'buildingIds'
  | 'classOfUses'
  | 'energyCertificateTypes'
  | 'typeOfUses'
  | 'energySourceTypes'
  | 'euTaxonomyCompliance'
  | 'monumentProtections'
  | 'heritageDistrict'
  | 'milieuProtection'
  | 'leasehold'
  | 'buildingStates'
  | 'districtHeatingAvailability'
  | 'isLod2'
  | 'responsibleUserIds'
  | 'economicUnitResponsibleUserIds'
>;

type CheckboxSearchParams = FilterByValue<Writable<(typeof BuildingFilters)[number]>, 'showAs', 'checkbox'>;
export type CheckboxSearchParamsArray = CheckboxSearchParams[];

type Props<Key extends CheckboxSearchParamKeys> = {
  title: React.ReactNode;
  type: Key;
  options: inferParserType<typeof BUILDING_FILTERS_SEARCH_PARAMS>[Key];
  selectedOptions: inferParserType<typeof BUILDING_FILTERS_SEARCH_PARAMS>[Key];
  getLabelForItem?: (
    item: FromArray<NonNullable<inferParserType<typeof BUILDING_FILTERS_SEARCH_PARAMS>[Key]>>,
  ) => React.ReactNode;
};
const CheckboxFilter = function CheckboxFilter<Key extends CheckboxSearchParamKeys>({
  title,
  type,
  options,
  selectedOptions,
  getLabelForItem,
}: Props<Key>) {
  const { setFilters } = useBuildingFilters();

  const handleSelect = useCallback(
    (
      value: FromArray<NonNullable<inferParserType<typeof BUILDING_FILTERS_SEARCH_PARAMS>[Key]>>,
      isChecked: boolean,
    ) => {
      void setFilters((prev) => {
        const prevValues = prev[type] ?? [];
        let nextValues;
        if (isChecked) {
          nextValues = [...prevValues, value];
        } else {
          nextValues = prevValues.filter((it) => it !== value);
        }

        return { ...prev, [type]: nextValues.length === 0 ? null : nextValues };
      });
    },
    [setFilters, type],
  );

  if (!options || options.length === 0) {
    return null;
  }

  return (
    <Collapsible title={title}>
      <Stack spacing={1} mt={-1}>
        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 1,
            width: '100%',
          }}
        >
          {options.map((item, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={selectedOptions?.some((it) => it === item)}
                  onChange={(e) => handleSelect(item, e.target.checked)}
                />
              }
              value={item}
              label={getLabelForItem ? getLabelForItem(item) : <FilterValueLabel type={type} value={item} />}
            />
          ))}
        </FormGroup>
      </Stack>
    </Collapsible>
  );
};

export default memo(CheckboxFilter, isEqual);
